import './PreloadCheckDetails.scss';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import React, { useEffect, useState } from 'react';
import { MenuItem, Select, Snackbar } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Stores
// Constants
import Mock from '../../constants/Mock';
import { ComplianceLevelConst, Status } from '../../constants/Status';
import { PreloadCheckQuestionStatusEnums, PreloadCheckStatus } from '../../enums/Status.enum';
// Shared
import CheckDetails from '../../shared/components/CheckDetails/CheckDetails';
import AddEditModal from '../../shared/components/add-edit-modal/Add-Edit-Modal';
import Loader from '../../shared/components/Loader/Loader';
// Interfaces
import {
  IPreloadCheck,
  IPreloadCheckPayload,
  IPreloadCheckPrintableData,
  IPreloadCheckQuestion,
  IPreloadCheckQuestionPayload,
} from '../../interfaces/PreloadCheck.interface';
import {
  IAnnualVettingRejectedRequirementDataTable,
  IColumnTable,
  IRejectedRequirementDataTable,
} from '../../interfaces/Table.interface';
import {
  IFormData,
  IFormSchema,
  IInitialValues,
  IOptions
} from '../../interfaces/Form-Modal.interface';
import { ICard, ICardItem } from '../../interfaces/Details.interface';
import { IPrintFooter } from '../../interfaces/Print.interface';
import { IManagerPayload } from '../../interfaces/Manager.interface';
// Services
import {
  FilterUnacceptableQuestions,
  editPreloadCheckQuestion,
  editPreloadCheckStatus,
  getPreloadCheck,
  getPreloadCheckCategories,
} from '../../services/PreloadCheck.service';
import { getPropertyAsElements, getPropertyAsString } from '../../services/Helper.service';
import { getCountryEmailRecipientListByName } from '../../services/Country.service';
import { useSnackbar } from '../../services/SnackbarUtils';
import { ExportData } from '../../services/Export.service';
// Svg
import IconStatusApproved from './../../assets/icons/icon-status-approved.svg';
import IconStatusRejected from './../../assets/icons/icon-status-rejected.svg';
import OryxLogo from './../../assets/images/oryx-energies-print.png';
import { sendEmail } from '../../services/Manager.service';

const formSchema: IFormSchema = Yup.object({
  manager: Yup.object().required(),
  comment: Yup.string(),
});

const formInitialValues: IInitialValues = {
  manager: '',
  comment: '',
}

const PreloadCheckDetails = (): JSX.Element => {
  const {
    AnnualVettingQuestionsColumns,
    CheckQuestionsColumns,
    EscalationModalForm, 
    PreloadCheckQuestionsColumns,
  } = Mock();
  // States
  const [preloadCheckData, setPreloadCheckData] = useState<IPreloadCheck>();
  const [dataTable, setDataTable] = useState<IRejectedRequirementDataTable[]>([]);
  const [PreloadCheckPrintableData, setPreloadCheckPrintableData] = useState<IPreloadCheckPrintableData[]>([]);
  const [annualVettingRejectedRequirementDataTable, setAnnualVettingRejectedRequirementDataTable] = useState<IAnnualVettingRejectedRequirementDataTable[]>([]);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const { snackBarOpen, snackbarMessage, snackBarType, showSnackbar, handleSnackBarClose } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAnnualVetting = Boolean(searchParams.get('isAnnualVetting'));
  const parentTab = searchParams.get('parentTab');
  const { t } = useTranslation('common');
  const params = useParams();
  const [EscalationForm, setEscalationForm] = useState<IFormData[]>(
    JSON.parse(JSON.stringify(EscalationModalForm))
  );
  const [escalationRecipientsCount, setEscalationRecipientsCount] = useState<number>();
  const [cardMainInfo, setCardMainInfo] = useState<ICardItem[]>([]);
  const [cardDriverInfo, setCardDriverInfo] = useState<ICardItem[]>([]);
  const [cardAgentInfo, setCardAgentInfo] = useState<ICardItem[]>([]);
  const [exportUrl, setExportUrl] = useState<string>();
  const [openModal, setOpenModal] = useState(false);
  const [componentRef, setComponentRef] = React.useState<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef,
    onAfterPrint: () => {
      componentRef?.setAttribute('style', 'display:none;');
    },
    onBeforeGetContent: () => {
      componentRef?.setAttribute('style', 'display:flex;');
    },
  });

  // Safely format full name
  const formatFullName = (person?: { name?: string; last_name?: string }) : string => {
    return person ? `${person.name ?? ''} ${person.last_name ?? ''}`.trim() : '';
  }

  const cards: ICard[] = [
    {
      title: t('main_information'),
      card: cardMainInfo,
    },
    {
      title: t('driver_information'),
      card: cardDriverInfo,
    },
    {
      title: t('agent_information'),
      card: cardAgentInfo,
    },
  ];
  
  const CheckQuestionsEditableStatusColumnTable: IColumnTable[] = [
    ...(CheckQuestionsColumns as IColumnTable[]),
    {
      accessorKey: 'status',
      header: t('status'),
      Cell: (data: any) => {
        return (
          <div className="status-select">
            <div className="status-container">{getStatusIcon(data.row.original.status)}</div>
            <Select
              className="table-status-select"
              labelId="table-status-select-standard-label"
              id="table-status-select-standard"
              value={
                data.row.original.status === PreloadCheckQuestionStatusEnums.ACCEPTABLE
                  ? 'yes'
                  : 'no'
              }
              onChange={(e) => {
                handleSelectStatusChange(e, data.row.original);
              }}
              MenuProps={{
                classes: { paper: 'oryx-select__paper table-status-select__paper' },
              }}
            >
              <MenuItem value="yes">{t('yes')}</MenuItem>
              <MenuItem value="no">{t('no')}</MenuItem>
            </Select>
          </div>
        );
      },
    },
  ];

  const getStatusIcon = (status: PreloadCheckQuestionStatusEnums): React.JSX.Element => {
    switch (status) {
      case PreloadCheckQuestionStatusEnums.ACCEPTABLE:
        return <img src={IconStatusApproved} alt="icon-status" />;
      default:
        return <img src={IconStatusRejected} alt="icon-status" />;
    }
  };

  const handleSelectStatusChange = (event: any, data: any): void => {
    setIsRequestInProgress(true);

    const selectedValue = event.target.value;
    const questionPayload: IPreloadCheckQuestionPayload = {
      preload_check_id: params.id as string,
      question_id: data.question_id,
      status: PreloadCheckQuestionStatusEnums.ACCEPTABLE,
      comment: data.comments,
    };

    if (selectedValue === 'no') {
      questionPayload.status = PreloadCheckQuestionStatusEnums.UNACCEPTABLE;
    }

    editPreloadCheckQuestion(data.id, questionPayload)
      .then(() => {
        setIsRequestInProgress(false);
        // Remove Question From Reject Requirements Table When it's edited
        const questionId = data.id;
        const newRejectedRequirementsDataTable = dataTable.filter((obj) => obj.id !== questionId);
        setDataTable(newRejectedRequirementsDataTable);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  /* METHOD EMITTING API CALLS */
  /* Get Preload Check Data Method */
  const fetchPreloadCheck = (): void => {
    setIsRequestInProgress(true);
    getPreloadCheck(params.id as string)
      .then((preloadCheck) => {
        setIsRequestInProgress(false);
        setPreloadCheckData(preloadCheck);

        getCardMainInfo(preloadCheck);
        getCardDriverInfo(preloadCheck);
        getCardAgentInfo(preloadCheck);
        // SET EXPORT URL
        setExportUrl(`/preload-check/export/detail/${preloadCheck.id}`);
        // The escalation recipients depend on the check's country, so the modal
        // can only be populated once the check has loaded.
        void SetModalForm(preloadCheck.country);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleEditPreloadCheckStatus = (status: PreloadCheckStatus): void => {
    if (preloadCheckData !== undefined) {
      const pcDetailsintermediateSecurityCheckStatus =
        preloadCheckData.security_check_status === PreloadCheckStatus.PENDINGREQUEST
          ? status
          : PreloadCheckStatus.TODO;

      setIsRequestInProgress(true);
      const preloadCheckPayload: IPreloadCheckPayload = {
        id: params.id as string,
        transporter: preloadCheckData.transporter,
        trailers: preloadCheckData.trailers,
        created_at: preloadCheckData.created_at,
        updated_at: preloadCheckData.updated_at,
        status,
        security_check_status:
          status === PreloadCheckStatus.REJECTED
            ? PreloadCheckStatus.REJECTED
            : pcDetailsintermediateSecurityCheckStatus,
      };
      editPreloadCheckStatus(params.id as string, preloadCheckPayload)
        .then((preloadCheckEdited) => {
          // Edit Preload CHeck Statue Status
          setPreloadCheckData({ ...preloadCheckData, status: preloadCheckEdited.status });

          let pcDetailsSnackBarMsg = 'preload_check_has_been_approved';
          let pcDetailsSnackBarClass = 'success';

          if (status === PreloadCheckStatus.REJECTED) {
            pcDetailsSnackBarMsg = 'preload_check_has_been_rejected';
            pcDetailsSnackBarClass = 'error';
          }

          showSnackbar(
            t(pcDetailsSnackBarMsg, { orderNumber: getPropertyAsString(preloadCheckData, 'orders', 'order_number') }),
            pcDetailsSnackBarClass
          );
          setIsRequestInProgress(false);
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });
    }
  };

  const FetchRejectedRequirements = (questions: IPreloadCheckQuestion[]): void => {
    if (isAnnualVetting) {
      getAnnualVettingRejectedRequiremmentsDataTable(questions);
    } else {
      getRejectedRequiremmentsDataTable(questions);
    }
  };

  const FetchPreloadCheckCategories = (): void => {
    setIsRequestInProgress(true);
    getPreloadCheckCategories(params.id as string)
      .then((categories) => {
        setIsRequestInProgress(false);
        const categoriesData = categories.map((categorie) => {
          return {
            title: categorie.category,
            rejectedQuestions: FilterUnacceptableQuestions(
              categorie.questions,
              categorie.category_id
            ),
          };
        });
        setPreloadCheckPrintableData(categoriesData);

        // Script To Retrieve Failed Requirements
        const failedQuestions: IPreloadCheckQuestion[] = [];

        categories.forEach((category) => {
          const filteredQuestions = FilterUnacceptableQuestions(
            category.questions,
            category.category_id
          );

          failedQuestions.push(...filteredQuestions);
        });

        if (failedQuestions.length > 0) {
          FetchRejectedRequirements(failedQuestions);
        }
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const getRejectedRequiremmentsDataTable = (
    rejectedRequirement: IPreloadCheckQuestion[]
  ): void => {
    const rejectedRequirementDataTable: IRejectedRequirementDataTable[] = rejectedRequirement.map(
      (question) => {
        return {
          id: question.id,
          question_id: question.question_id,
          tolerance: question.tolerance as string,
          category: question.category_id as string,
          requirement: question.question,
          comments: question.comment,
          photo: question.image,
          status: question.status,
        };
      }
    );

    setDataTable(rejectedRequirementDataTable);
  };

  const getAnnualVettingRejectedRequiremmentsDataTable = (
    rejectedRequirement: IPreloadCheckQuestion[]
  ): void => {
    const rejectedRequirementDataTable: IAnnualVettingRejectedRequirementDataTable[] =
      rejectedRequirement.map((question) => {
        return {
          id: question.id,
          question_id: question.question_id,
          category: question.category_id as string,
          requirement: question.question,
          comments: question.comment,
          subCategory: question.subCategory,
          photo: question.image,
          compliance_level: question.eliminatoryComplianceLevels[0],
        };
      });

    setAnnualVettingRejectedRequirementDataTable(rejectedRequirementDataTable);
  };

  /* Fill Details Card Method */
  const getCardMainInfo = (preloadCheck: IPreloadCheck): void => {
    const cardMainInfoData: ICardItem[] = [
      {
        label: 'transporter',
        value: preloadCheck.transporter,
      },
      {
        label: 'truck_number',
        value: preloadCheck.truck?.truck_number ?? '',
      },
      {
        label: 'trailer_number',
        value: preloadCheck.trailers.length > 0 ? preloadCheck.trailers[0].registration_number : '',
      },
      {
        label: 'product',
        value: getPropertyAsElements(preloadCheck.products, 'description'),
      },
      {
        label: 'date',
        value: moment(preloadCheck.created_at).format('YYYY-MM-DD'),
      },
    ];

    // ORDER NUMBER IS NOT SUPPORTED ON ANNUAL VETTINGS
    if (!isAnnualVetting) {        
      setCardMainInfo([
        {
          label: 'order_number',
          value: getPropertyAsElements(preloadCheck.orders, 'order_number'),
        },
        ...cardMainInfoData,
      ]);
    } else {
      setCardMainInfo(cardMainInfoData);
    }
  };

  const getCardDriverInfo = (preloadCheck: IPreloadCheck): void => {
    const cardDriverInfoData: ICardItem[] = [
      {
        label: 'name',
        value: preloadCheck.driver.name ? preloadCheck.driver.name : '' + ' ' + preloadCheck.driver.last_name ? preloadCheck.driver.last_name : preloadCheck.driver.last_name,
      },
      {
        label: 'id_number',
        value: preloadCheck.driver?.card_id ?? '',
      },
      {
        label: 'driver_licence',
        value: preloadCheck.driver?.driver_licence ?? '',
      },
      {
        label: 'licence_expiration',
        value: moment(preloadCheck.driver.exp_licence).format('YYYY-MM-DD'),
      },
    ];

    setCardDriverInfo(cardDriverInfoData);
  };

  const getCardAgentInfo = (preloadCheck: IPreloadCheck): void => {
    const cardAgentInfoData: ICardItem[] = [
      {
        label: 'country',
        value: preloadCheck.agent?.country ?? '',
      },
      {
        label: 'depot',
        value: preloadCheck.agent?.depot ?? '',
      },
      {
        label: 'agent',
        value: preloadCheck.agent?.name ?? '',
      },
      {
        label: 'situation',
        value: 
          preloadCheck.agent?.is_external 
            ? preloadCheck.agent.is_external
              ? t('agent_extern')
              : t('agent_intern')
            : '',
      },
      {
        label: 'time_spent',
        value: preloadCheck.agent?.preload_check_average_time ?? '',
      },
      {
        label: 'security_agent',
        value: preloadCheck.security_agent?.name ?? '',
      },
      {
        label: 'time_spent',
        value: preloadCheck.security_agent?.preload_check_average_time ?? '',
      },
    ];

    setCardAgentInfo(cardAgentInfoData);
  };

  const redirectToEntirePreloadCheck = (): void => {
    let queryParams = '';

    if (parentTab !== null) {
      queryParams = `?parentTab=${parentTab}`;
    }

    if (isAnnualVetting) {
      queryParams += `${queryParams.length > 0 ? '&' : '?'}isAnnualVetting=true`;
    }

    navigate(`/preload-checks/entire-check/${params.id as string}${queryParams}`);
  };

  /* Export Method */
  const PreloadCheckExport = (): any => {
    if (exportUrl !== undefined) {
      setIsRequestInProgress(true);
      ExportData(exportUrl)
        .then(() => {
          setIsRequestInProgress(false);
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });
    }
  };

  const redirectToEntireSecurityCheck = (): void => {
    let queryParams = '';

    if (parentTab !== null) {
      queryParams += `&parentTab=${parentTab}`;
    }

    if (isAnnualVetting) {
      queryParams += '&isAnnualVetting=true';
    }

    navigate(
      `/preload-checks/entire-check/${params.id as string}?isSecurityCheck=true${queryParams}`
    );
  };

  const isCheckDone = (): boolean => {
    return preloadCheckData?.status !== Status.TODO;
  };

  const GetComplianceLevel = (complianceLevel: string): string => {
    switch (complianceLevel) {
      case ComplianceLevelConst.LEVEL_1:
        return '1';
      case ComplianceLevelConst.LEVEL_2:
        return '2';
      case ComplianceLevelConst.LEVEL_3:
        return '3';
      default:
        return '4';
    }
  };

  const PrintTemplate = (preloadCheck: IPreloadCheck): JSX.Element => {
    const printFooter: IPrintFooter[] = [
      {
        role: t('inspector'),
        label: formatFullName(preloadCheck.agent),
        date: moment(preloadCheck.created_at).format('YYYY-MM-DD'),
      },
      {
        role: t('safety_supervisor'),
        label: formatFullName(preloadCheck.security_agent),
        date: moment(preloadCheck.created_at).format('YYYY-MM-DD'),
      },
      {
        role: t('driver'),
        label: formatFullName(preloadCheck.driver),
      },
    ];

    return (
      <div
        className="print-template"
        ref={(el) => {
          setComponentRef(el);
        }}
      >
        <div className="print-template__top">
          <div className="print-template__header">
            <img src={OryxLogo} alt="oryx-logo" />
            <div className="block-title">
              <p className="subtitle">ORYX ENERGIES</p>
              <h1 className="title">{t('check_list_trucks')}</h1>
            </div>
            <div className="block-desc">
              <p>OE-DS-FO-011-V01-E</p>
              <p>{moment().format('DD MMMM YYYY')}</p>
            </div>
          </div>

          <div className="print-template__body">
            <div className="card-preload-check">
              <div className="card-right">
                <div className="card-item">
                  <p className="label">{t('check_list_number')}</p>
                  <p className="value">{preloadCheck.id}</p>
                </div>
                <div className="card-item">
                  <p className="label">{t('date')}</p>
                  <p className="value">{moment(preloadCheck.created_at).format('YYYY-MM-DD')}</p>
                </div>
                {!isAnnualVetting && (
                  <div className="card-item">
                    <p className="label">{t('erp_loading_number')}</p>
                    <p className="value">{getPropertyAsElements(preloadCheck.orders, 'order_number')}</p>
                  </div>
                )}
                <div className="card-item">
                  <p className="label">{t('transporter')}</p>
                  <p className="value">Oryx Energies</p>
                </div>
              </div>
              <div className="card-left">
                <div className="card-item">
                  <p className="label">{t('driver_name')}</p>
                  <p className="value">
                    {preloadCheck.driver.name + ' ' + preloadCheck.driver.last_name}
                  </p>
                </div>
                <div className="card-item">
                  <p className="label">{t('product_transported')}</p>
                  <p className="value">{getPropertyAsElements(preloadCheck.products, 'description')}</p>
                </div>
                <div className="card-item">
                  <p className="label">{t('tractor_regist_numer')}</p>
                  <p className="value">{preloadCheck.truck.truck_number}</p>
                </div>
                <div className="card-item">
                  <p className="label">{t('trailer_regist_numer')}</p>
                  <p className="value">
                    {preloadCheck.trailers.length > 0
                      ? preloadCheck.trailers[0].registration_number
                      : '-'}
                  </p>
                </div>
                <div className="card-item">
                  <p className="label">{t('capacity_of_the_tank')}</p>
                  <p className="value">
                    {preloadCheck.trailers.length > 0
                      ? preloadCheck.trailers[0].capacity + ' ' + preloadCheck.trailers[0].unit
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
            <div className="card-categories">
              <p className="card-categories__title">{t('categories_and_rejected_requirements')}</p>
              <div className="card-categories__item">
                {PreloadCheckPrintableData.map((category, index) => {
                  return (
                    <div className="category-item" key={`category-${index}`}>
                      <h2 className="category-title">{category.title}</h2>
                      {category.rejectedQuestions.length > 0 ? (
                        <div className="rejected-requirements">
                          {category.rejectedQuestions.map((question) => {
                            return (
                              <div className="rejected-requirements__item" key={question.id}>
                                <div className="labels">
                                  <p className="label requirement">{t('requirement')}</p>
                                  {!isAnnualVetting && <p className="label">{t('tolerance')}</p>}
                                  {isAnnualVetting && (
                                    <p className="label">{t('compliance_level')}</p>
                                  )}
                                  <p className="label">{t('comment')}</p>
                                </div>
                                <div className="values">
                                  <p className="value">{question.question}</p>
                                  {!isAnnualVetting && (
                                    <p className="value">{question.tolerance}</p>
                                  )}
                                  {isAnnualVetting && (
                                    <p className="value">
                                      {GetComplianceLevel(question.eliminatoryComplianceLevels[0])}
                                    </p>
                                  )}
                                  <p className="value">{question.comment}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="all-requirements-met">{t('all_requirements_met')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="print-template__bottom">
          <div className="truck-acceptance">
            <div className="truck-acceptance__item">
              <p className="label">{t('truck_accepted_required')}</p>
              <div className="value">{preloadCheck.status === 'approved' ?  'x' : '' }</div>
            </div>
            <div className="truck-acceptance__item">
              <p className="label">{t('truck_rejected_required')}</p>
              <div className="value">{preloadCheck.status === 'rejected' ?  'x' : '' }</div>
            </div>
          </div>
          
          <div className="block-signature">
            <p className="block-signature__title">{t('name_signature_and_date')}</p>
            <div className="block-signature__items">
              {printFooter.map((item, index) => (
                <div className="block-signature__item" key={`footer-print-item-${index}`}>
                  <p className="role">{item.role}</p>
                  <div className="signature-box">
                    <span className="label">{item.label}</span>
                  </div>
                  {item.date && (
                    <div className="date">
                      <p className="label">{t('automatic_date')}</p>
                      <p className="value">{item.date}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* Escalation Modal */
  const onCloseModal = (): void => {
    setOpenModal(false);
  };
  
  const openBtnModal = (): void => {
    setOpenModal(true);
  };
    
  const handleEscalatePreloadCheck = (formData: IFormSchema): void => {
    if (preloadCheckData === undefined) {
      showSnackbar('Preload check data unavailable.', 'error');
      return;
    }

    setIsRequestInProgress(true);

    const escalatePayload: IManagerPayload = {
      to: formData.manager.name,
      subject: 'New Preload Check Escalation Notification',
      text: 'A new preload check has been required your approval.',
      template: 'escalation',
      data: {
        organization: 'Oryx Energies',
        username: formData.manager.label,
        preloadCheck: {
          order_number: getPropertyAsString(preloadCheckData, 'orders', 'order_number'),
          comment: formData.comment,
          country: preloadCheckData.country,
          depot: preloadCheckData.depot,
          createdAt: preloadCheckData.created_at,
          url: window.location.href,
        },
      },
    };

    sendEmail(escalatePayload)
        .then(() => {
          setIsRequestInProgress(false);
          setOpenModal(false);
          showSnackbar(t('escalation_has_been_sent_successfully'), 'success');
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });
  };

  /**
   * Escalation recipients come from the country configuration managed in the
   * back office, so what the manager can pick always matches what is configured
   * for that country. `country` is passed through raw: the API normalizes casing
   * and spacing server side.
   */
  const SetModalForm = async (country?: string): Promise<void> => {
    const formData = EscalationForm;

    if (country === undefined || country.trim() === '') {
      setEscalationRecipientsCount(0);
      return;
    }

    try {
      const recipientList = await getCountryEmailRecipientListByName(country);
      const options: IOptions[] = recipientList.data.map((recipient) => ({
        value: recipient.id,
        label: recipient.email,
        name: recipient.email,
      }));

      const managerFieldIndex = formData.findIndex((field) => field.name === 'manager');
      if (managerFieldIndex !== -1) {
        formData[managerFieldIndex].options = options;
      }

      setEscalationRecipientsCount(options.length);
      setEscalationForm(formData);
    } catch (error: any) {
      setEscalationRecipientsCount(0);
      showSnackbar(error.message, 'error');
    }
  };

  useEffect(() => {
    fetchPreloadCheck();
    FetchPreloadCheckCategories();
  }, []);

  return (
    <div className="preload-check-details">
      {isRequestInProgress && <Loader />}
      {preloadCheckData !== undefined && PrintTemplate(preloadCheckData)}
      {preloadCheckData !== undefined && (
        <div className="details-container">
          <CheckDetails
            isAnnualVetting={isAnnualVetting}
            title={
              isAnnualVetting ? preloadCheckData.truck.truck_number : getPropertyAsString(preloadCheckData, 'orders', 'order_number', true, 2)
            }
            pcStatus={preloadCheckData.status}
            handleApprovalClicked={() => {
              handleEditPreloadCheckStatus(PreloadCheckStatus.APPROVED);
            }}
            handleEscalateClicked={() => {
              if (escalationRecipientsCount === 0) {
                showSnackbar(t('no_managers_configured_for_country'), 'error');
                return;
              }
              openBtnModal();
            }}
            handleRejectionClicked={() => {
              handleEditPreloadCheckStatus(PreloadCheckStatus.REJECTED);
            }}
            subtitle={isAnnualVetting ? t('annual_vettings') : t('preload_checks')}
            cards={cards}
            isCheckDone={isCheckDone()}
            rejectedRequirementsDataTable={dataTable}
            rejectedRequirementsColumnsTable={
              preloadCheckData.status === PreloadCheckStatus.PENDINGREQUEST
                ? CheckQuestionsEditableStatusColumnTable
                : (PreloadCheckQuestionsColumns as IColumnTable[])
            }
            annualVettingRejectedRequirementDataTable={annualVettingRejectedRequirementDataTable}
            annualVettingRejectedRequirementsColumnsTable={
              AnnualVettingQuestionsColumns as IColumnTable[]
            }
            rejectedRequirementsTableTotalPages={0}
            onTableRowClicked={() => {}}
            handleViewEntirePreloadCheckClicked={redirectToEntirePreloadCheck}
            handleViewEntireSecurityCheckClicked={redirectToEntireSecurityCheck}
            handleExport={exportUrl !== undefined ? PreloadCheckExport : undefined}
            handlePrint={handlePrint}
          />
        </div>
      )}

      <Snackbar
        className={`oryx-snackbar ${snackBarType}`}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
      />

      <AddEditModal
        modalTitle={t('escalate')}
        modalForm={EscalationForm}
        formSchema={formSchema}
        formInitialValues={formInitialValues}
        openModal={openModal}
        onClose={onCloseModal}
        handleSubmit={handleEscalatePreloadCheck}
        closeConfirmationModalTitle={t('are_you_sure')}
        closeConfirmationModalDescription={''}
      />
    </div>
  );
};

export default PreloadCheckDetails;
